import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';

// Initialize Datadog Logs and Distributed Tracing (RUM disabled for cost savings)
export const initDatadog = () => {
  const applicationId = process.env.REACT_APP_DATADOG_APPLICATION_ID;
  const clientToken = process.env.REACT_APP_DATADOG_CLIENT_TOKEN;

  if (!clientToken) {
    console.warn('Datadog credentials not configured. Monitoring disabled.');
    return;
  }

  // Initialize RUM with minimal config for distributed tracing only
  // sessionSampleRate: 0 disables RUM data collection but keeps trace header injection
  if (applicationId) {
    datadogRum.init({
      applicationId,
      clientToken,
      site: 'datadoghq.com',
      service: 'dental-portal',
      env: process.env.REACT_APP_TEMP_MEE_ENV || 'development',
      version: process.env.REACT_APP_VERSION || '1.0.0',
      sessionSampleRate: 0, // Disabled until backend tracing infrastructure is ready
      sessionReplaySampleRate: 0, // Disable session replay
      trackUserInteractions: false,
      trackResources: false,
      trackLongTasks: false,
      traceSampleRate: 100, // Keep distributed tracing enabled
      allowedTracingUrls: [
        process.env.REACT_APP_BASE_URL,
        process.env.REACT_APP_BASE_URL_NEW,
      ].filter(Boolean).map(url => ({
        match: url,
        propagatorTypes: ['datadog', 'tracecontext'],
      })),
    });
  }

  // Initialize Logs - this is the main feature we're keeping
  datadogLogs.init({
    clientToken,
    site: 'datadoghq.com',
    forwardErrorsToLogs: true,
    forwardConsoleLogs: ['error'], // Only forward console.error to Datadog
    sessionSampleRate: 100,
    service: 'dental-portal',
    env: process.env.REACT_APP_TEMP_MEE_ENV || 'development',
    version: process.env.REACT_APP_VERSION || '1.0.0',
  });
};

// Set user context for Logs
export const setDatadogUser = (user) => {
  if (!user) {
    datadogLogs.clearUser();
    return;
  }

  const userContext = {
    id: user.id?.toString(),
  };

  // Remove undefined values
  Object.keys(userContext).forEach(key =>
    userContext[key] === undefined && delete userContext[key]
  );

  datadogLogs.setUser(userContext);
};

// Clear user context (for logout)
export const clearDatadogUser = () => {
  datadogLogs.clearUser();
};

// Centralized logging utilities
// Only errors are sent to Datadog to reduce noise and cost
export const logger = {
  error: (message, context = {}) => {
    console.error(message, context);
    datadogLogs.logger.error(message, context);
  },

  warn: (message, context = {}) => {
    console.warn(message, context);
    // Not sent to Datadog - local only
  },

  info: (message, context = {}) => {
    console.info(message, context);
    // Not sent to Datadog - local only
  },

  debug: (message, context = {}) => {
    if (process.env.NODE_ENV === 'development') {
      console.debug(message, context);
    }
    // Not sent to Datadog - local only
  },
};

// Track errors with additional context
export const captureException = (error, context = {}) => {
  const errorContext = {
    ...context,
    errorMessage: error?.message || String(error),
    errorStack: error?.stack,
    errorName: error?.name,
  };

  logger.error('Application Error', {
    error,
    ...errorContext,
  });
};